import React, { FC, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { gqlCountryEnum as COUNTRY, gqlProfileStatusEnum as PROFILE_STATUS } from '@minecraft.graphql-types';
import { AppType } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import { FlexCol, BodyTextLarge } from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import Connector from './Connector';
import ProfileHeader from './ProfileHeader';
import RolePreferences from './RolePreferences';
import Appearance from './Appearance';
import Credits from './Credits';
import Resume from './Resume';
import Skills from './Skills';
import Assets from './Assets';
import OtherExperience from './OtherExperience';
import Sizes from './Sizes';
import Details from './Details';
import Summary from './Summary';

import ProfileViewContext from './ProfileViewContext';
import ProfileTopNav from './ProfileTopNav';
import ProfileInfo from './ProfileInfo';
import Media from './Media';

/** Styled Components */
const PageWrapper = styled.div`
  border: 0.125rem solid ${(props): string => props.theme.colors.primary.ltGrey1};
  padding: 2rem 2rem 0;
  @media screen and (max-width: 767px) {
    border: 0;
    position: relative;
    padding: 0;
    margin-top: 0;
    font-size: 1rem;
  }
`;

/** Types */
type NoProfileMessageProps = {
  isPublic: boolean;
};

type Props = {
  appType: AppType;
  profileId?: number;
  roleId?: number;
  folderId?: number;
  accountId?: number;
  isPreview?: boolean;
  showRepDetails?: boolean;
  showSummary?: boolean;
  showSummaryWithSubmissionDetails?: boolean;
  showSummaryWithFolderCustomization?: boolean;
  isAdmin?: boolean;
  onEditClick?: null | ((arg?: any) => any);
  isRearrangeMedia?: boolean;
  hidePreviewButtons?: boolean;
  isPlayableAgeSectionHidden?: boolean;
  selectedDivisionId?: number;
  isPublic?: boolean;
  guid?: string;
  children: ReactNode;
  noExit?: boolean;
  accountCountryCode?: COUNTRY;
  userName?: string;
  userNameModalToggle?: () => void;
  presentationGuid?: string;
  presentationId?: number;
  pickedBy?: { id: string; useName: number }[];
  pickCount?: number;
  showPicks?: boolean;
};

/** Helper Components */
const InactivePublicProfileMessage: FC = () => {
  const { t } = useTranslation();

  return (
    <FlexCol alignItems="center" gap="8">
      <Icon name="no-profile-404" />
      <BodyTextLarge fontWeight="medium">{t('common:toast.inactivePublicProfile')}</BodyTextLarge>
    </FlexCol>
  );
};

const NoProfileMessage: FC<NoProfileMessageProps> = ({ isPublic }) => {
  const { t } = useTranslation();

  if (isPublic) return <InactivePublicProfileMessage />;

  return <div>{t('common:toast.noProfile')}</div>;
};

/** Helpers */
const inactiveProfileStatuses = [
  PROFILE_STATUS.VERIFY_AGE,
  PROFILE_STATUS.CREATE_PROFILE_STEP_1,
  PROFILE_STATUS.INACTIVE,
];

/** Main Component */
const ProfileView = ({
  accountCountryCode,
  accountId = null,
  appType,
  children,
  folderId = null,
  guid = null,
  hidePreviewButtons = false,
  isAdmin = false,
  isPlayableAgeSectionHidden = false,
  isPreview = false,
  isPublic = false,
  isRearrangeMedia = false,
  noExit = false,
  onEditClick = null,
  pickCount,
  pickedBy,
  presentationGuid,
  presentationId,
  profileId = null,
  roleId = null,
  selectedDivisionId = null,
  showPicks,
  showRepDetails = false,
  showSummary = false,
  showSummaryWithFolderCustomization = false,
  showSummaryWithSubmissionDetails = false,
  userName,
  userNameModalToggle,
}: Props): JSX.Element => {
  const { hash } = useLocation();

  return (
    <Connector
      accountCountryCode={accountCountryCode}
      accountId={accountId}
      appType={appType}
      folderId={folderId}
      guid={guid}
      hasUrlHash={Boolean(hash)}
      isPublic={isPublic}
      profileId={profileId}
      roleId={roleId}
      selectedDivisionId={selectedDivisionId}
      showSummaryWithFolderCustomization={showSummaryWithFolderCustomization}
      showSummaryWithSubmissionDetails={showSummaryWithSubmissionDetails}
      isPreview={isPreview}
    >
      {({ fetcher, updater }): JSX.Element => {
        const profileMessage =
          isPublic && inactiveProfileStatuses.includes(fetcher?.profile?.profileStatus?.code) ? (
            <InactivePublicProfileMessage />
          ) : (
            <PageWrapper>{children}</PageWrapper>
          );

        return fetcher.profile ? (
          <ProfileViewContext.Provider
            value={{
              appType,
              isAdmin,
              isPreview,
              showRepDetails,
              showSummary,
              showSummaryWithSubmissionDetails,
              showSummaryWithFolderCustomization,
              onEditClick,
              accountId,
              fetcher,
              updater,
              hidePreviewButtons,
              isRearrangeMedia,
              isPlayableAgeSectionHidden,
              isPublic,
              noExit,
              userName,
              userNameModalToggle,
              presentationId,
              presentationGuid,
              pickedBy,
              pickCount,
              showPicks,
              accountCountryCode,
            }}
          >
            {profileMessage}
          </ProfileViewContext.Provider>
        ) : (
          <NoProfileMessage isPublic />
        );
      }}
    </Connector>
  );
};

ProfileView.Header = ProfileHeader;
ProfileView.TopNav = ProfileTopNav;
ProfileView.Info = ProfileInfo;
ProfileView.Summary = Summary;
ProfileView.Media = Media;
ProfileView.Credits = Credits;
ProfileView.Details = Details;
ProfileView.Resume = Resume;
ProfileView.Skills = Skills;
ProfileView.Assets = Assets;
ProfileView.OtherExperience = OtherExperience;
ProfileView.Sizes = Sizes;
ProfileView.Appearance = Appearance;
ProfileView.RolePreferences = RolePreferences;

ProfileView.Context = ProfileViewContext;

export default ProfileView;
